import React from 'react'
import { Box, Paper, Typography, Skeleton, Tooltip, useTheme, alpha } from '@mui/material'
import { TrendingUp, TrendingDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const KPICard = ({ label, value, icon: Icon, metric, accentColor, loading, format: formatFn }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const goldColor = accentColor || theme.palette.primary.main

  const changePct = metric?.change_pct
  const isPositive = changePct !== null && changePct !== undefined && changePct >= 0
  const displayValue = formatFn ? formatFn(metric?.value ?? value) : (metric?.value ?? value ?? 0)
  const changeLabel = changePct !== null && changePct !== undefined
    ? `${changePct > 0 ? '+' : ''}${changePct}%`
    : null

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: '12px',
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          borderColor: alpha(goldColor, 0.4),
          boxShadow: `0 4px 16px ${alpha(goldColor, 0.08)}`,
          transform: 'translateY(-1px)',
        },
      }}
    >
      {Icon && (
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: '10px',
            background: alpha(goldColor, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: goldColor,
            flexShrink: 0,
          }}
        >
          <Icon size={20} strokeWidth={1.75} />
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '0.72rem',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            mb: 0.5,
          }}
        >
          {label}
        </Typography>

        {loading ? (
          <>
            <Skeleton variant="text" width={60} height={32} />
            <Skeleton variant="text" width={50} height={18} sx={{ mt: 0.5 }} />
          </>
        ) : (
          <>
            <Tooltip title={String(displayValue)} enterDelay={300} disableHoverListener={String(displayValue).length < 20}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.3rem', sm: '1.5rem' },
                  color: theme.palette.text.primary,
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {displayValue}
              </Typography>
            </Tooltip>

            {changeLabel && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.75 }}>
                {isPositive ? (
                  <TrendingUp size={14} style={{ color: theme.palette.success.main }} />
                ) : (
                  <TrendingDown size={14} style={{ color: theme.palette.error.main }} />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: isPositive ? theme.palette.success.main : theme.palette.error.main,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                  }}
                >
                  {changeLabel}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.disabled, fontSize: '0.7rem' }}
                >
                  {t('analytics_vs_prev_period')}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Paper>
  )
}

export default KPICard
